import { Search, X } from 'lucide-react';
import { SearchInput } from '@/components/ui/search-input';
import { cn } from '@/lib/utils';

interface MobilePillSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  ariaLabel?: string;
  /** Small ghost text shown right-aligned inside the pill when input is empty. */
  rightHint?: string;
}

/**
 * Standard mobile pill search bar used across Inventory, Market, Storefront,
 * and eBay Marketplace toolbars.
 *
 * Visual contract:
 *  - h-9 rounded-full pill (matches MobileFilterPillButton / MobileSortPill)
 *  - bg-black/30 with subtle white border
 *  - left magnifier icon, right Clear-X when value present
 *  - 16px text (no iOS zoom on focus; native shell also blocks zoom gestures)
 */
export function MobilePillSearchInput({
  value,
  onChange,
  placeholder = 'Search...',
  className,
  ariaLabel,
  rightHint,
}: MobilePillSearchInputProps) {
  const showHint = !value && rightHint;
  return (
    <div className={cn('relative', className)}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4 pointer-events-none z-10" />
      <SearchInput
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel ?? placeholder}
        className={cn(
          'w-full h-9 pl-9 text-base bg-black/30 border-white/[0.08] rounded-full',
          value ? 'pr-9' : showHint ? 'pr-3' : 'pr-9'
        )}
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange('')}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground z-10"
          aria-label="Clear search"
        >
          <X className="w-4 h-4" />
        </button>
      ) : showHint ? (
        <span
          className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground/60 pointer-events-none z-10 max-w-[55%] truncate"
          aria-hidden="true"
        >
          {rightHint}
        </span>
      ) : null}
    </div>
  );
}
